import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Check, Crown, Lock, ArrowLeft, Loader2, PartyPopper, Users, Music2, Heart, Sparkles, Infinity as InfinityIcon, QrCode } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { Header } from "@/components/Header";
import { LiyaPataDivider } from "@/components/Motifs";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = 250;
const EXTRA = 150;

export default function Membership() {
  const { fontClass, lang } = useLang();
  const [cfg, setCfg] = useState(null);
  const [langs, setLangs] = useState(["si"]);
  const [plan, setPlan] = useState("annual");
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [otpSent, setOtpSent] = useState(false);
  const [otpBusy, setOtpBusy] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [demoCode, setDemoCode] = useState("");
  const [listenerToken, setListenerToken] = useState("");

  const phoneValid = phone.trim().replace(/\D/g, "").length >= 9;
  const verified = !!listenerToken;

  const sendOtp = async () => {
    setOtpBusy(true);
    try {
      const { data } = await axios.post(`${API}/listener/request-otp`, { phone });
      setOtpSent(true);
      setDemoCode(data.demo ? data.demo_code : "");
      toast.success(data.demo ? "Demo mode: your code is shown below." : "We sent a code to your phone.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send the code");
    } finally { setOtpBusy(false); }
  };

  const verifyOtp = async () => {
    setOtpBusy(true);
    try {
      const { data } = await axios.post(`${API}/listener/verify-otp`, { phone, code: otpCode });
      setListenerToken(data.token);
      toast.success("Phone verified 🎉");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code");
    } finally { setOtpBusy(false); }
  };

  useEffect(() => {
    axios.get(`${API}/membership/config`).then((r) => setCfg(r.data)).catch(() => setCfg({ enabled: false }));
  }, []);

  const toggleLang = (c) => setLangs((p) => (p.includes(c) ? p.filter((x) => x !== c) : [...p, c]));
  const n = langs.length;
  const monthly = BASE + Math.max(n - 1, 0) * EXTRA;
  const price = plan === "annual" ? monthly * 10 : monthly;

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/membership/start-trial`, { phone, name, languages: langs, plan }, { headers: { Authorization: `Bearer ${listenerToken}` } });
      setDone(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start membership");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <Header />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10 pb-28">
        <Link to="/rooms/sindu-kamare" data-testid="membership-back" className="inline-flex items-center gap-2 font-ui text-sm font-bold mb-6" style={{ color: "var(--text-2)" }}>
          <ArrowLeft size={16} /> Back
        </Link>

        {/* Premium "Join the family" showcase — shown in every state */}
        <div className="rounded-3xl overflow-hidden mb-8 relative sk-luxe" data-testid="membership-hero"
          style={{ background: "var(--membership-card-bg)", border: "1px solid rgba(255,215,120,0.4)", boxShadow: "0 26px 64px -30px rgba(123,44,191,0.7)" }}>
          <div className="h-[3px] w-full sk-shimmer" />
          <LiyaPataDivider className="absolute top-6 right-5 w-28 opacity-30 pointer-events-none" style={{ color: "#FFD873" }} />
          <div className="relative px-6 py-9 sm:px-9 sm:py-11">
            <span className="grid place-items-center w-16 h-16 rounded-2xl mb-5 sk-pulse-glow" style={{ background: "rgba(255,215,120,0.16)", color: "#FFD873", border: "1px solid rgba(255,215,120,0.4)" }}>
              <Crown size={30} />
            </span>
            <p className="font-ui text-xs font-bold tracking-[0.2em] uppercase mb-2 flex items-center gap-2" style={{ color: "rgba(255,216,115,0.85)" }}>
              Neox · සංගීත පවුල
              <span className="sk-gem gold" aria-hidden="true" /><span className="sk-gem" aria-hidden="true" /><span className="sk-gem gold" aria-hidden="true" />
            </p>
            <h1 className={`${lang === "si" ? "font-si" : "sk-display"} text-3xl sm:text-4xl font-extrabold leading-tight`} style={{ color: "#FFFFFF" }}>
              {lang === "si" ? "පවුලට එක්වන්න" : lang === "ta" ? "குடும்பத்தில் இணையுங்கள்" : "Join the family"}
            </h1>
            <p className="font-ui text-base sm:text-lg mt-3 max-w-md" style={{ color: "rgba(245,243,255,0.86)" }}>
              {lang === "si" ? "එක කාමරයක් · සෑම පරම්පරාවක්ම. මුත්තණුවන්ගේ සිට මුණුබුරන් දක්වා — එකම ගෙදරක්." : lang === "ta" ? "ஒரே அறை · ஒவ்வொரு தலைமுறையும். தாத்தா பாட்டி முதல் பேரக்குழந்தைகள் வரை — ஒரே குடும்பம்." : "One room. Every generation. From grandparents to grandchildren — all one family."}
            </p>
            <p className="font-ui text-base sm:text-lg italic font-semibold mt-3 max-w-md" data-testid="mem-royal-line">
              <span className="sk-gold-foil">
                {lang === "si" ? "මෙහි ඔබ රජෙක්, බිසොවක් — ඔබ ආදරය කරන සංගීතයෙන් ඔටුන්න පළඳින්න." : lang === "ta" ? "இங்கே நீங்கள் அரசர், அரசி — நீங்கள் விரும்பும் இசையால் முடிசூடுங்கள்." : "Here, you reign — a king, a queen — crowned by the music you love."}
              </span>
            </p>

            <ul className="mt-7 space-y-3.5">
              {[
                { icon: Music2, t: "The whole music room — ad-free, forever" },
                { icon: Users, t: "Up to 6 loved ones on one gentle price" },
                { icon: Heart, t: "Private karaoke & dedications for your family" },
                { icon: Sparkles, t: "Rare archive recordings & folk chants (ජනකවි)" },
                { icon: InfinityIcon, t: "Cancel anytime · your first month free" },
              ].map((v, i) => {
                const Icon = v.icon;
                return (
                  <li key={i} className="flex items-center gap-3" data-testid={`mem-value-${i}`}>
                    <span className="grid place-items-center w-8 h-8 rounded-full shrink-0" style={{ background: "rgba(255,215,120,0.15)", color: "#FFD873" }}><Icon size={16} /></span>
                    <span className="font-ui text-base" style={{ color: "rgba(245,243,255,0.92)" }}>{v.t}</span>
                  </li>
                );
              })}
            </ul>

            <div className="mt-7 inline-flex items-baseline gap-2 px-4 py-2.5 rounded-2xl" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,215,120,0.3)" }}>
              <span className="font-display text-2xl font-extrabold sk-gold-foil">Rs {BASE.toLocaleString()}</span>
              <span className="font-ui text-sm" style={{ color: "rgba(245,243,255,0.7)" }}>/ month · first month free</span>
            </div>
          </div>
        </div>

        {!cfg && <div className="flex justify-center py-20"><Loader2 className="animate-spin" style={{ color: "var(--accent)" }} /></div>}

        {/* Pay by LankaQR — scannable by any bank app, BOC SmartPay, Dialog eZ Cash, Mobitel mCash */}
        <div className="rounded-3xl overflow-hidden mb-8 sk-gold-edge sk-luxe" data-testid="lankaqr-pay">
          <div className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-5">
              <span className="grid place-items-center w-11 h-11 rounded-2xl" style={{ background: "var(--brass-light)", color: "var(--accent)" }}><QrCode size={22} /></span>
              <div>
                <h2 className="font-display text-xl font-extrabold" style={{ color: "var(--text)" }}>{lang === "si" ? "LankaQR එකෙන් ගෙවන්න" : lang === "ta" ? "LankaQR மூலம் செலுத்துங்கள்" : "Pay by LankaQR"}</h2>
                <p className="font-ui text-sm" style={{ color: "var(--text-muted)" }}>{lang === "si" ? "ඕනෑම බැංකු යෙදුමකින් ස්කෑන් කරන්න" : lang === "ta" ? "எந்த வங்கி செயலியிலும் ஸ்கேன் செய்யுங்கள்" : "Scan with any bank app"}</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <div className="rounded-2xl bg-white p-4 shrink-0" style={{ border: "1px solid var(--border)" }}>
                <img src="/lankaqr-swaranjaliya.png" alt="LankaQR — TEC Sri Lanka World Wide" className="w-48 h-auto" data-testid="lankaqr-image" />
              </div>
              <div className="text-center sm:text-left">
                <p className="font-ui text-base font-bold" style={{ color: "var(--text)" }}>TEC Sri Lanka World Wide (Pvt)</p>
                <p className="font-ui text-sm mt-1" style={{ color: "var(--text-2)" }}>
                  {lang === "si" ? "BOC LankaQR · ඕනෑම බැංකු යෙදුමකින් ස්කෑන් කළ හැක" : lang === "ta" ? "BOC LankaQR · எந்த வங்கி செயலியிலும் ஸ்கேன் செய்யலாம்" : "BOC LankaQR · scannable with any bank app"}
                </p>
                <p className="font-ui text-sm mt-4 leading-relaxed" style={{ color: "var(--text-muted)" }}>
                  {lang === "si"
                    ? "ස්කෑන් කර Rs 250 ගෙවා, රිසිට්පත WhatsApp කරන්න — අපි ඔබේ පවුලේ සාමාජිකත්වය සක්‍රීය කරමු. 🌸"
                    : lang === "ta"
                    ? "ஸ்கேன் செய்து Rs 250 செலுத்தி, ரசீதை WhatsApp செய்யுங்கள் — உங்கள் குடும்ப உறுப்பினர் சேர்க்கப்படும். 🌸"
                    : "Scan, pay Rs 250, and WhatsApp us the receipt — we'll activate your family membership. 🌸"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {cfg && !cfg.enabled && (
          <div className="rounded-3xl p-8 text-center" style={{ background: "var(--card)", border: "1px solid var(--border)" }} data-testid="membership-locked">
            <span className="grid place-items-center w-14 h-14 rounded-full mx-auto" style={{ background: "var(--brass-light)", color: "var(--accent)" }}><Sparkles size={24} /></span>
            <h2 className={`${fontClass()} text-xl font-bold mt-4`} style={{ color: "var(--text)" }}>Opening our doors soon 🌸</h2>
            <p className="font-ui text-base mt-2" style={{ color: "var(--text-muted)" }}>Everything above is ready. The family membership opens shortly — keep listening free in the meantime, there's always room for you.</p>
            <Link to="/rooms/sindu-kamare" className="inline-block mt-6 h-12 px-6 leading-[3rem] rounded-full font-ui font-bold" style={{ background: "var(--accent)", color: "#fff" }}>Back to the music</Link>
          </div>
        )}

        {cfg && cfg.enabled && done && (
          <div className="rounded-3xl p-8 text-center" style={{ background: "var(--card)", border: "2px solid var(--border)" }} data-testid="membership-success">
            <span className="grid place-items-center w-16 h-16 rounded-full mx-auto" style={{ background: "var(--brass-light)", color: "var(--accent)" }}><PartyPopper size={28} /></span>
            <h1 className={`${fontClass()} text-2xl font-bold mt-4`} style={{ color: "var(--text)" }}>Your first month is free! 🎉</h1>
            <p className="font-ui text-sm mt-2" style={{ color: "var(--text-muted)" }}>{done.message}</p>
            <p className="font-ui text-xs mt-3" style={{ color: "var(--text-muted)" }}>After the free month: Rs {price.toLocaleString()} / {plan === "annual" ? "year" : "month"}.</p>
            <Link to="/rooms/sindu-kamare" className="inline-block mt-6 h-12 px-6 leading-[3rem] rounded-full font-ui font-bold" style={{ background: "var(--accent)", color: "#fff" }}>Start listening</Link>
          </div>
        )}

        {cfg && cfg.enabled && !done && (
          <div data-testid="membership-form">
            <div className="text-center mb-8">
              <h2 className={`${fontClass()} text-2xl font-bold`} style={{ color: "var(--text)" }}>Complete your family membership</h2>
              <p className="font-ui text-base mt-1" style={{ color: "var(--text-muted)" }}>First month free · cancel anytime</p>
            </div>

            <p className="font-ui text-sm font-bold mb-2" style={{ color: "var(--text-2)" }}>Choose your language room(s)</p>
            <div className="grid grid-cols-3 gap-3 mb-6">
              {(cfg.languages || []).map((l) => {
                const on = langs.includes(l.code);
                return (
                  <button key={l.code} type="button" data-testid={`mem-lang-${l.code}`} onClick={() => toggleLang(l.code)}
                    className="h-14 rounded-2xl font-ui font-bold text-sm flex items-center justify-center gap-1.5 transition-transform active:scale-95"
                    style={{ background: on ? "var(--accent)" : "var(--card)", color: on ? "#fff" : "var(--text-2)", border: `2px solid ${on ? "var(--accent)" : "var(--border)"}` }}>
                    {on && <Check size={15} />} {l.label}
                  </button>
                );
              })}
            </div>

            <div className="grid grid-cols-2 gap-3 mb-6">
              {[{ k: "monthly", t: "Monthly" }, { k: "annual", t: "Annual · 2 months free" }].map((o) => {
                const on = plan === o.k;
                return (
                  <button key={o.k} type="button" data-testid={`mem-plan-${o.k}`} onClick={() => setPlan(o.k)}
                    className="p-4 rounded-2xl text-left transition-transform active:scale-95"
                    style={{ background: on ? "var(--brass-light)" : "var(--card)", border: `2px solid ${on ? "var(--accent)" : "var(--border)"}` }}>
                    <span className="font-ui text-xs font-bold" style={{ color: "var(--text-muted)" }}>{o.t}</span>
                    <div className={`${fontClass()} text-xl font-bold mt-1`} style={{ color: "var(--text)" }}>
                      Rs {(o.k === "annual" ? monthly * 10 : monthly).toLocaleString()}
                      <span className="text-xs font-semibold" style={{ color: "var(--text-muted)" }}> /{o.k === "annual" ? "yr" : "mo"}</span>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="rounded-2xl p-4 mb-6 text-center" style={{ background: "var(--card)", border: "1px solid var(--border)" }} data-testid="mem-price">
              <p className="font-ui text-sm" style={{ color: "var(--text-muted)" }}>{n} language{n > 1 ? "s" : ""} · {plan}</p>
              <p className={`${fontClass()} text-2xl font-bold`} style={{ color: "var(--accent)" }}>Rs {price.toLocaleString()} <span className="text-sm" style={{ color: "var(--text-muted)" }}>after free month</span></p>
            </div>

            <div className="space-y-3 mb-4">
              <input data-testid="mem-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name"
                className="w-full h-14 px-5 rounded-2xl font-ui outline-none" style={{ background: "var(--card)", border: "2px solid var(--border)", color: "var(--text)" }} />
              <div className="flex gap-2">
                <input data-testid="mem-phone" value={phone} onChange={(e) => { setPhone(e.target.value); setOtpSent(false); setListenerToken(""); }} placeholder="Phone number (07XXXXXXXX)" inputMode="tel" disabled={verified}
                  className="flex-1 h-14 px-5 rounded-2xl font-ui outline-none disabled:opacity-70" style={{ background: "var(--card)", border: `2px solid ${verified ? "var(--accent)" : "var(--border)"}`, color: "var(--text)" }} />
                {!verified && (
                  <button onClick={sendOtp} disabled={!phoneValid || otpBusy} data-testid="mem-send-otp"
                    className="h-14 px-4 rounded-2xl font-ui font-bold text-sm whitespace-nowrap disabled:opacity-50" style={{ background: "var(--brass-light)", color: "var(--accent)", border: "2px solid var(--accent)" }}>
                    {otpBusy ? <Loader2 size={18} className="animate-spin" /> : otpSent ? "Resend" : "Send code"}
                  </button>
                )}
                {verified && <span className="h-14 px-4 grid place-items-center rounded-2xl font-ui font-bold text-sm" style={{ background: "var(--accent)", color: "#fff" }} data-testid="mem-phone-verified"><Check size={18} /> Verified</span>}
              </div>
            </div>

            {otpSent && !verified && (
              <div className="rounded-2xl p-4 mb-6" style={{ background: "var(--card)", border: "2px solid var(--border)" }} data-testid="mem-otp-box">
                <p className="font-ui text-sm font-bold mb-2" style={{ color: "var(--text-2)" }}>Enter the 6-digit code</p>
                {demoCode && (
                  <p className="font-ui text-xs mb-3 px-3 py-2 rounded-xl" style={{ background: "var(--brass-light)", color: "var(--accent)" }} data-testid="mem-demo-code">
                    Demo mode — your code is <b style={{ letterSpacing: "3px" }}>{demoCode}</b> (a real SMS is sent when we launch).
                  </p>
                )}
                <div className="flex gap-2">
                  <input data-testid="mem-otp-input" value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000" inputMode="numeric"
                    className="flex-1 h-14 px-5 rounded-2xl font-ui outline-none tracking-[6px] text-center text-xl" style={{ background: "var(--bg)", border: "2px solid var(--border)", color: "var(--text)" }} />
                  <button onClick={verifyOtp} disabled={otpCode.length < 6 || otpBusy} data-testid="mem-verify-otp"
                    className="h-14 px-5 rounded-2xl font-ui font-bold disabled:opacity-50" style={{ background: "var(--accent)", color: "#fff" }}>
                    {otpBusy ? <Loader2 size={18} className="animate-spin" /> : "Verify"}
                  </button>
                </div>
              </div>
            )}

            <button onClick={start} disabled={busy || n < 1 || !verified} data-testid="mem-start"
              className="w-full h-14 rounded-full font-ui font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-60"
              style={{ background: "var(--accent)", color: "#fff" }}>
              {busy ? <Loader2 size={20} className="animate-spin" /> : <Crown size={20} />} Start my free month
            </button>
            <p className="font-ui text-xs text-center mt-3" style={{ color: "var(--text-muted)" }}>{verified ? `No charge today · Rs ${price.toLocaleString()} only after your free month` : "Verify your phone number to continue"}</p>
          </div>
        )}
      </div>
    </div>
  );
}
